import React, {
  Dispatch,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { usePageLoadMetricsContext } from './PageLoadMetrics';

export type PageLoadStart = {
  startTime: number;
};

export type PageLoadEnd = {
  endTime: number;
};

type PageLoadContextType = {
  pageLoadEnd: PageLoadEnd;
  pageLoadStart: PageLoadStart;
  setPageLoadEnd: Dispatch<React.SetStateAction<PageLoadEnd>>;
  setPageLoadStart: Dispatch<React.SetStateAction<PageLoadStart>>;
};

const PageLoadTimeContext = createContext<PageLoadContextType>(
  {} as PageLoadContextType
);
PageLoadTimeContext.displayName = 'PageLoadTimeContext';

function PageLoadEndTimeMarker() {
  const { setPageLoadEnd } = useContext(PageLoadTimeContext);

  useEffect(() => {
    setPageLoadEnd((currentPageLoadEnd) => ({
      ...currentPageLoadEnd,
      endTime: performance.now(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <></>;
}

export type PageLoadTimeProviderProps = {
  children?: React.ReactNode;
  initialEndTime?: PageLoadEnd;
  initialStartTime?: PageLoadStart;
};

export function PageLoadTimeProvider(
  props: Readonly<PageLoadTimeProviderProps>
) {
  const [pageLoadStart, setPageLoadStart] = useState<PageLoadStart>(
    props.initialStartTime || {
      startTime: performance.now(),
    }
  );

  const [pageLoadEnd, setPageLoadEnd] = useState<PageLoadEnd>(
    props.initialEndTime || {
      endTime: 0,
    }
  );

  const { routeTimer } = usePageLoadMetricsContext();
  const location = useLocation();

  useEffect(() => {
    if (
      pageLoadEnd.endTime &&
      routeTimer?.stopDuration &&
      location.pathname === routeTimer.pathname
    ) {
      routeTimer.stopDuration(routeTimer);
    }
  }, [location.pathname, pageLoadEnd.endTime, routeTimer]);

  useEffect(() => {
    if (pageLoadEnd.endTime) {
      try {
        datadogRum.addAction('IngramPageLoadTime', {
          endTime: pageLoadEnd.endTime,
          loadTime: pageLoadEnd.endTime - pageLoadStart.startTime,
          route: window.location.pathname,
          startTime: pageLoadStart.startTime,
        });
      } catch (error) {
        console.error('Error posting to Datadog:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoadEnd.endTime]);

  const providerValue = useMemo(
    () => ({
      pageLoadEnd,
      pageLoadStart,
      setPageLoadEnd,
      setPageLoadStart,
    }),
    [pageLoadStart, pageLoadEnd]
  );

  return (
    <PageLoadTimeContext.Provider value={providerValue}>
      {props.children}
      <PageLoadEndTimeMarker />
    </PageLoadTimeContext.Provider>
  );
}

export default PageLoadTimeContext;
